// RuntimeDock.tsx — Horizontal inline bar design

import React, { useMemo, useState, useEffect, useRef } from "react";
import { useRuntime } from '../../context/RuntimeContext.tsx';
import {
  Loader2,
  ChevronUp,
  ChevronDown,
  Clock3,
  CheckCircle2,
  AlertTriangle,
  Square,
  ExternalLink,
  Globe,
  FileSpreadsheet,
  Code2,
  Bot,
  Cpu,
} from "lucide-react";

type RuntimeStatus = "running" | "queued" | "completed" | "failed";

export interface RuntimeTask {
  id: string;
  agentName: string;
  title?: string;
  status: RuntimeStatus;
  progress?: number;
  startedAt?: string;
  duration?: string;
  currentStep?: string;
  model?: string;
  tool?: string;
  icon?: React.ReactNode;
}

interface RuntimeDockProps {
  tasks: RuntimeTask[];
  onOpenMissionControl?: () => void;
  onStopTask?: (taskId: string) => void;
  initiallyExpanded?: boolean;
}

// ── Colors ─────────────────────────────────────────────
const T = {
  bg: "var(--lyra-color-surface)",
  border: "var(--lyra-color-border)",
  borderHover: "var(--app-border-strong)",
  ink: "var(--lyra-color-text)",
  ink2: "var(--app-text-2)",
  ink3: "var(--lyra-color-text-muted)",
  ink4: "var(--app-text-3)",
  green: "var(--lyra-color-success)",
  greenBg: "var(--lyra-color-success-bg)",
  greenDot: "var(--lyra-color-success)",
  amber: "var(--lyra-color-warning)",
  amberBg: "var(--lyra-color-warning-bg)",
  red: "var(--lyra-color-danger)",
  redBg: "var(--lyra-color-danger-bg)",
  accent: "var(--lyra-color-primary)",
  accentBg: "var(--app-primary-soft)",
  surface: "var(--lyra-color-surface-2)",
  pillBg: "var(--app-surface-3)",
};

function getStatusColor(status: RuntimeStatus) {
  switch (status) {
    case "running":  return { dot: T.greenDot, ring: T.green };
    case "queued":   return { dot: T.amber, ring: T.amber };
    case "completed":return { dot: T.accent, ring: T.accent };
    case "failed":   return { dot: T.red, ring: T.red };
    default:         return { dot: T.ink4, ring: T.ink4 };
  }
}

// Default icon mapping based on agent name keywords
function getAgentIcon(agentName: string, status: RuntimeStatus): React.ReactNode {
  const n = agentName.toLowerCase();
  const size = 16;
  const color = getStatusColor(status).dot;
  if (n.includes("research") || n.includes("web") || n.includes("search"))
    return <Globe size={size} color={color} />;
  if (n.includes("excel") || n.includes("sheet") || n.includes("csv"))
    return <FileSpreadsheet size={size} color={color} />;
  if (n.includes("cod") || n.includes("dev") || n.includes("buddy"))
    return <Code2 size={size} color={color} />;
  if (n.includes("cpu") || n.includes("model"))
    return <Cpu size={size} color={color} />;
  return <Bot size={size} color={color} />;
}

// ── Spinner dot ────────────────────────────────────────
function StatusIndicator({ status }: { status: RuntimeStatus }) {
  const { dot } = getStatusColor(status);
  if (status === "running") {
    return (
      <div style={{ position: "relative", width: 10, height: 10, flexShrink: 0 }}>
        <div
          style={{
            width: 10, height: 10, borderRadius: "50%",
            background: dot, position: "relative", zIndex: 1,
          }}
        />
        <div
          style={{
            position: "absolute", inset: -3,
            borderRadius: "50%",
            border: `2px solid ${dot}`,
            opacity: 0.35,
            animation: "runtimePulse 1.8s infinite",
          }}
        />
      </div>
    );
  }
  if (status === "queued") {
    return (
      <div style={{ width: 10, height: 10, borderRadius: "50%", background: dot, flexShrink: 0 }} />
    );
  }
  if (status === "completed") {
    return <CheckCircle2 size={14} color={dot} style={{ flexShrink: 0 }} />;
  }
  if (status === "failed") {
    return <AlertTriangle size={14} color={dot} style={{ flexShrink: 0 }} />;
  }
  return <div style={{ width: 10, height: 10, borderRadius: "50%", background: T.ink4, flexShrink: 0 }} />;
}

// ── Agent card (one slot in the bar) ──────────────────
const AgentCard = React.memo(function AgentCard({
  task,
  onStop,
  isLast,
}: {
  task: RuntimeTask;
  onStop: (id: string) => void;
  isLast: boolean;
}) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "0 20px",
        borderRight: isLast ? "none" : `1px solid ${T.border}`,
        minWidth: 0,
        flexShrink: 0,
        height: "100%",
        transition: "background 0.15s",
        background: hovered ? T.surface : "transparent",
        cursor: "default",
      }}
    >
      {/* Agent icon in a pill */}
      <div
        style={{
          width: 34, height: 34,
          borderRadius: 10,
          background: T.pillBg,
          border: `1px solid ${T.border}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {task.icon ?? getAgentIcon(task.agentName, task.status)}
      </div>

      {/* Text */}
      <div style={{ minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span
            style={{
              fontSize: 13, fontWeight: 600,
              color: T.ink, whiteSpace: "nowrap",
              overflow: "hidden", textOverflow: "ellipsis",
              maxWidth: 130,
            }}
          >
            {task.agentName}
          </span>
        </div>
        <div
          style={{
            fontSize: 11.5, color: T.ink3,
            whiteSpace: "nowrap", overflow: "hidden",
            textOverflow: "ellipsis", maxWidth: 140,
            marginTop: 1,
          }}
        >
          {task.currentStep || (task.status === "queued" ? "Queued…" : task.status === "completed" ? "Done" : "Processing…")}
        </div>
      </div>

      {/* Status indicator */}
      <StatusIndicator status={task.status} />

      {/* Stop button (shown on hover for running tasks) */}
      {task.status === "running" && hovered && (
        <button
          onClick={(e) => { e.stopPropagation(); onStop(task.id); }}
          title="Stop task"
          style={{
            width: 24, height: 24, borderRadius: 6,
            border: `1px solid ${T.border}`,
            background: T.redBg, color: T.red,
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", flexShrink: 0, marginLeft: 2,
          }}
        >
          <Square size={10} />
        </button>
      )}
    </div>
  );
});

// ── Main RuntimeDock ───────────────────────────────────
export default function RuntimeDock({
  tasks = [],
  onOpenMissionControl,
  onStopTask,
  initiallyExpanded = false,
}: RuntimeDockProps) {
  const runtime = useRuntime();
  const expanded = runtime.state.expanded;
  const dispatch = runtime.dispatch;

  const runningCount = useMemo(
    () => tasks.filter(t => t.status === "running" || t.status === "queued").length,
    [tasks]
  );
  const failedCount = useMemo(() => tasks.filter(t => t.status === "failed").length, [tasks]);
  const hasActivity = runningCount > 0;

  // Pick visible tasks (max ~4 before overflow)
  const visibleTasks = tasks.slice(0, 4);
  const overflow = tasks.length - visibleTasks.length;

  if (!expanded) {
    // Collapsed: tiny pill in the corner
    return (
      <div
        style={{
          position: "fixed", bottom: 16, right: 16,
          zIndex: 1000,
        }}
      >
          <button
            onClick={() => dispatch({ type: 'SET_EXPANDED', expanded: true })}
          title="Show Agent Runtime"
          style={{
            display: "flex", alignItems: "center", gap: 8,
            padding: "7px 12px",
            borderRadius: 999,
            background: T.bg,
            border: `1px solid ${T.border}`,
            boxShadow: "0 4px 14px rgba(15,23,42,0.08)",
            cursor: "pointer",
            fontSize: 12, fontWeight: 600, color: T.ink,
          }}
        >
          <div style={{ position: "relative", width: 8, height: 8 }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: hasActivity ? T.greenDot : failedCount > 0 ? T.red : T.ink4 }} />
            {hasActivity && (
              <div style={{ position: "absolute", inset: -3, borderRadius: "50%", border: `2px solid ${T.greenDot}`, opacity: 0.4, animation: "runtimePulse 1.8s infinite" }} />
            )}
          </div>
          Runtime
          {runningCount > 0 && (
            <span style={{ padding: "1px 7px", borderRadius: 999, background: T.greenBg, color: T.green, fontSize: 10.5, fontWeight: 700 }}>
              {runningCount}
            </span>
          )}
          <ChevronUp size={13} color={T.ink3} />
        </button>
        <style>{keyframes}</style>
      </div>
    );
  }

  return (
    <div
      style={{
        position: "fixed",
        bottom: 0, left: 0, right: 0,
        zIndex: 1000,
        display: "flex",
        justifyContent: "center",
        pointerEvents: "none",
      }}
    >
      <div
        style={{
          pointerEvents: "auto",
          width: "100%",
          maxWidth: 1100,
          margin: "0 16px 12px",
          height: 60,
          borderRadius: 16,
          background: T.bg,
          border: `1px solid ${T.border}`,
          boxShadow: "0 4px 24px rgba(0,0,0,0.09), 0 1px 4px rgba(0,0,0,0.06)",
          display: "flex",
          alignItems: "stretch",
          overflow: "hidden",
        }}
      >
        {/* ── Left: Runtime label ── */}
        <div
          style={{
            display: "flex", alignItems: "center", gap: 10,
            padding: "0 18px",
            borderRight: `1px solid ${T.border}`,
            flexShrink: 0,
            minWidth: 148,
          }}
        >
          {/* Green pulsing dot */}
          <div style={{ position: "relative", width: 10, height: 10, flexShrink: 0 }}>
            <div
              style={{
                width: 10, height: 10, borderRadius: "50%",
                background: hasActivity ? T.greenDot : failedCount > 0 ? T.red : T.ink4,
              }}
            />
            {hasActivity && (
              <div
                style={{
                  position: "absolute", inset: -3,
                  borderRadius: "50%",
                  border: `2px solid ${T.greenDot}`,
                  opacity: 0.35,
                  animation: "runtimePulse 1.8s infinite",
                }}
              />
            )}
          </div>

          <div>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: T.ink, lineHeight: 1.2 }}>
              Runtime
            </div>
            <div style={{ fontSize: 11, color: T.ink3, marginTop: 2 }}>
              {runningCount > 0
                ? `${runningCount} agent${runningCount > 1 ? "s" : ""} running`
                : tasks.length === 0
                ? "No active tasks"
                : "All complete"}
            </div>
          </div>

          {/* Collapse chevron */}
          <button
            onClick={() => dispatch({ type: 'SET_EXPANDED', expanded: false })}
            style={{
              marginLeft: "auto",
              width: 24, height: 24, borderRadius: 6,
              border: `1px solid ${T.border}`,
              background: T.pillBg, color: T.ink3,
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", flexShrink: 0,
            }}
          >
            <ChevronDown size={13} />
          </button>
        </div>

        {/* ── Center: Agent cards ── */}
        <div
          style={{
            display: "flex",
            alignItems: "stretch",
            flex: 1,
            minWidth: 0,
            overflowX: "auto",
          }}
        >
          {tasks.length === 0 ? (
            <div style={{ display: "flex", alignItems: "center", padding: "0 20px", color: T.ink4, fontSize: 13 }}>
              No agents active
            </div>
          ) : (
            visibleTasks.map((task, i) => (
              <AgentCard
                key={task.id}
                task={task}
                onStop={onStopTask ?? (() => {})}
                isLast={i === visibleTasks.length - 1 && overflow === 0}
              />
            ))
          )}

          {/* Overflow indicator */}
          {overflow > 0 && (
            <div
              style={{
                display: "flex", alignItems: "center",
                padding: "0 16px",
                fontSize: 12.5, color: T.ink3, fontWeight: 600,
                borderRight: `1px solid ${T.border}`,
                flexShrink: 0,
              }}
            >
              +{overflow} more
            </div>
          )}
        </div>

        {/* ── Right: View all ── */}
        <div
          style={{
            display: "flex", alignItems: "center",
            padding: "0 14px",
            borderLeft: `1px solid ${T.border}`,
            gap: 8,
            flexShrink: 0,
          }}
        >
          <button
            onClick={onOpenMissionControl}
            style={{
              padding: "7px 16px",
              borderRadius: 10,
              border: `1.5px solid ${T.border}`,
              background: T.bg,
              color: T.ink2,
              fontSize: 12.5, fontWeight: 600,
              cursor: "pointer",
              display: "flex", alignItems: "center", gap: 5,
              whiteSpace: "nowrap",
              transition: "border-color 0.15s, background 0.15s",
            }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLButtonElement).style.borderColor = T.ink3;
              (e.currentTarget as HTMLButtonElement).style.background = T.surface;
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLButtonElement).style.borderColor = T.border;
              (e.currentTarget as HTMLButtonElement).style.background = T.bg;
            }}
          >
            View all
            <ExternalLink size={12} />
          </button>
        </div>
      </div>

      <style>{keyframes}</style>
    </div>
  );
}

const keyframes = `
  @keyframes runtimePulse {
    0%   { transform: scale(.7); opacity: .45; }
    70%  { transform: scale(1.5); opacity: 0; }
    100% { opacity: 0; }
  }
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;